import { allocateSeats } from '@val/engine'
import { getParty, type Phase } from '@val/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuthContext } from '../auth/AuthContext.tsx'
import { Hemicycle } from '../components/Hemicycle.tsx'
import { useAleResult } from '../hooks/useAleResult.ts'
import { ALE, ALE_MAJORITY, aleOpts, getHighlightParty } from '../lib/config.ts'
import { int, signed } from '../lib/format.ts'
import { CoalitionBuilder } from '../sim/CoalitionBuilder.tsx'
import { CommitteeSeats } from '../sim/CommitteeSeats.tsx'
import { MarginalTable } from '../sim/MarginalTable.tsx'
import { PartyVoteEditor } from '../sim/PartyVoteEditor.tsx'
import { SeatTransferTool } from '../sim/SeatTransferTool.tsx'
import {
  type BaseKind,
  base2022,
  createScenario,
  exportScenario,
  importScenario,
  loadScenarios,
  type Scenario,
  saveScenarios,
  votesFromResult,
} from '../sim/scenario.ts'
import '../sim/sim.css'

function allocate(votes: Record<string, number>) {
  const valid = Object.values(votes).reduce((a, b) => a + b, 0)
  const input = Object.entries(votes).map(([party, v]) => ({ party, votes: v }))
  const r = allocateSeats(input, aleOpts(valid))
  return { seats: r.seats, valid }
}

export function SimuleringPage() {
  const { authenticated, ready } = useAuthContext()
  const [params] = useSearchParams()
  const phaseOverride = (params.get('phase') as Phase | null) ?? undefined
  const { result } = useAleResult(phaseOverride, false)
  const [highlight] = useState(getHighlightParty)

  const [scenario, setScenario] = useState<Scenario>(() => createScenario('val-2022', base2022()))
  const [saved, setSaved] = useState<Scenario[]>(() => loadScenarios())
  const [compareId, setCompareId] = useState<string>('')
  const fileInput = useRef<HTMLInputElement>(null)
  const appliedLiveBase = useRef(false)
  // Aktuellt resultat är förvalt utgångsläge så fort det finns – ?base=val-2022
  // eller ?base=blank kan användas för att uttryckligen välja något annat.
  const requestedBase = params.get('base')
  const wantsLiveBase = requestedBase !== 'val-2022' && requestedBase !== 'blank'

  const setBase = useCallback(
    (kind: BaseKind) => {
      if (kind === 'live') {
        if (result) setScenario(createScenario('live', votesFromResult(result)))
        return
      }
      if (kind === 'val-2022') {
        setScenario(createScenario('val-2022', base2022()))
        return
      }
      setScenario(createScenario('blank', base2022()))
      setScenario((s) => ({
        ...s,
        votes: Object.fromEntries(Object.keys(s.votes).map((k) => [k, 0])),
      }))
    },
    [result],
  )

  const current = useMemo(() => allocate(scenario.votes), [scenario.votes])
  const baseAlloc = useMemo(() => allocate(scenario.baseVotes), [scenario.baseVotes])

  const setVotes = (code: string, votes: number) =>
    setScenario((s) => ({ ...s, votes: { ...s.votes, [code]: Math.max(0, Math.round(votes)) } }))

  const resetVotes = () => setScenario((s) => ({ ...s, votes: { ...s.baseVotes } }))

  const persist = (list: Scenario[]) => {
    setSaved(list)
    saveScenarios(list)
  }

  const saveCurrent = () => {
    const stamped = { ...scenario, updatedAt: new Date().toISOString() }
    const existing = saved.findIndex((s) => s.id === stamped.id)
    persist(
      existing >= 0 ? saved.map((s) => (s.id === stamped.id ? stamped : s)) : [...saved, stamped],
    )
    setScenario(stamped)
  }

  const compareScenario = saved.find((s) => s.id === compareId) ?? null
  const compareAlloc = compareScenario ? allocate(compareScenario.votes) : null

  useEffect(() => {
    if (wantsLiveBase && result && !appliedLiveBase.current) {
      appliedLiveBase.current = true
      setBase('live')
    }
  }, [wantsLiveBase, result, setBase])

  // Inget ska avslöja att simuleringsläget finns för utloggade – skicka till inloggning.
  if (ready && !authenticated) {
    return <Navigate to="/login" replace />
  }

  const highlightNow = current.seats[highlight] ?? 0
  const highlightBase = baseAlloc.seats[highlight] ?? 0

  return (
    <div className="simpage">
      <div className="simpage__head">
        <div>
          <h1>Simulering</h1>
          <p className="muted">
            Utgångsläge:{' '}
            <select
              value={scenario.baseKind}
              onChange={(e) => setBase(e.target.value as BaseKind)}
              className="pill-select"
            >
              <option value="live">Aktuellt resultat{result ? '' : ' (ej hämtat)'}</option>
              <option value="val-2022">Valet 2022</option>
              <option value="blank">Tomt</option>
            </select>
          </p>
        </div>
        <div className="simpage__scenario-bar">
          <input
            className="ve-number"
            style={{ width: 200 }}
            value={scenario.name}
            onChange={(e) => setScenario((s) => ({ ...s, name: e.target.value }))}
          />
          <button type="button" className="btn" onClick={saveCurrent}>
            Spara
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => navigator.clipboard?.writeText(exportScenario(scenario))}
            title="Kopiera scenariot som JSON"
          >
            Kopiera JSON
          </button>
          <button type="button" className="btn" onClick={() => fileInput.current?.click()}>
            Importera
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="application/json"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0]
              if (!f) return
              try {
                const s = importScenario(await f.text())
                setScenario(s)
                persist([...saved, s])
              } catch (err) {
                alert(err instanceof Error ? err.message : 'Kunde inte läsa filen')
              }
              e.target.value = ''
            }}
          />
        </div>
      </div>

      <div className="simpage__grid">
        <section className="card card__pad">
          <PartyVoteEditor
            votes={scenario.votes}
            baseVotes={scenario.baseVotes}
            seats={current.seats}
            baseSeats={baseAlloc.seats}
            highlightParty={highlight}
            onChange={setVotes}
            onReset={resetVotes}
          />
        </section>

        <aside className="card card__pad simpage__summary">
          <Hemicycle
            seatsByParty={current.seats}
            totalSeats={ALE.seats}
            majority={ALE_MAJORITY}
            highlightParty={highlight}
          />
          <p className="hemi-caption">
            {getParty(highlight).shortName}: <strong>{highlightNow}</strong> mandat{' '}
            <span className="dim">({signed(highlightNow - highlightBase)} mot utgångsläget)</span>
          </p>
          <p className="dim tnum" style={{ textAlign: 'center' }}>
            {int(current.valid)} röster i scenariot
          </p>
        </aside>
      </div>

      <section className="card card__pad">
        <div className="card__title">Koalitioner och majoritet</div>
        <CoalitionBuilder seats={current.seats} totalSeats={ALE.seats} highlightParty={highlight} />
      </section>

      <section className="card card__pad">
        <div className="card__title">Nämndplatser – proportionellt val</div>
        <CommitteeSeats seats={current.seats} highlightParty={highlight} />
      </section>

      <section className="card card__pad">
        <div className="card__title">Mandatbyte – hur nära?</div>
        <SeatTransferTool
          votes={scenario.votes}
          validVotes={current.valid}
          highlightParty={highlight}
        />
      </section>

      <section className="card card__pad">
        <div className="card__title">Marginaler</div>
        <MarginalTable
          votes={scenario.votes}
          validVotes={current.valid}
          highlightParty={highlight}
        />
      </section>

      {saved.length > 0 && (
        <section className="card card__pad">
          <div className="card__title">Jämför med sparat scenario</div>
          <select
            value={compareId}
            onChange={(e) => setCompareId(e.target.value)}
            className="pill-select"
          >
            <option value="">– välj –</option>
            {saved.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {compareScenario && compareAlloc && (
            <table className="margtable" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Parti</th>
                  <th>{scenario.name || 'Nuvarande'}</th>
                  <th>{compareScenario.name}</th>
                  <th>Skillnad</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(current.seats)
                  .filter((c) => (current.seats[c] ?? 0) + (compareAlloc.seats[c] ?? 0) > 0)
                  .sort((a, b) => getParty(a).order - getParty(b).order)
                  .map((c) => {
                    const a = current.seats[c] ?? 0
                    const b = compareAlloc.seats[c] ?? 0
                    return (
                      <tr key={c} className={c === highlight ? 'is-hi' : ''}>
                        <td>
                          <span
                            className="mini-chip"
                            style={{ background: getParty(c).color, color: getParty(c).onColor }}
                          >
                            {getParty(c).shortName}
                          </span>
                        </td>
                        <td className="tnum">{a}</td>
                        <td className="tnum">{b}</td>
                        <td className="tnum">{a !== b ? signed(a - b) : '–'}</td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {saved.length > 0 && (
        <section className="card card__pad">
          <div className="card__title">Sparade scenarier</div>
          <ul className="sim-saved">
            {saved.map((s) => (
              <li key={s.id}>
                <button type="button" className="linkbtn" onClick={() => setScenario(s)}>
                  {s.name}
                </button>
                <button
                  type="button"
                  className="linkbtn linkbtn--danger"
                  onClick={() => persist(saved.filter((x) => x.id !== s.id))}
                >
                  Ta bort
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
